import * as Sentry from '@sentry/react';
import { BenefitAlertSource } from 'shared/components/SidePanel/shared/types/enums/BenefitAlertSource';
import React, { ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { PanelViews } from 'shared/components/SidePanel/shared/types/enums/PanelViews';
import { IAddClientOrPatient } from 'shared/components/SidePanel/shared/types/interfaces/IAddClientOrPatient';
import { useSidePanel } from 'shared/components/SidePanel/state/providers/SidePanelProvider';
import { PersistedStateKeys } from 'shared/enums/PersistedStateKeys';
import useClinicUser from 'shared/hooks/useClinicUser';
import usePersistedState from 'shared/hooks/usePersistedState';
import usePrevious from 'shared/hooks/usePrevious';
import { SidePanelPetParentFragment } from 'shared/types/graphql';
import { RewardsTab } from '../../components/LoyaltyProgram/components/RewardsPanel';
import { Mode } from '../../shared/types/enum/Mode';
import { ISearchPanelContext } from '../contexts/ISearchPanelContext';
import SearchPanelContext from '../contexts/SearchPanelContext';
import { ModuleName, useModularity } from 'shared/hooks/useModularity';

export const useSidePanelSearch = (): ISearchPanelContext => useContext<ISearchPanelContext>(SearchPanelContext);
interface ISearchPanelProviderProps {
  children: ReactNode;
}

const SearchPanelProvider = ({ children }: ISearchPanelProviderProps): JSX.Element => {
  const [isSearching, setIsSearching] = useState(false);
  const [conversationId, setConversationId] = useState('');

  const [searchPanelMode, setSearchPanelMode] = usePersistedState<Mode>(PersistedStateKeys.SidePanelMode, Mode.Default);

  const [petNameSearch, setPetNameSearch] = usePersistedState<string>(
    PersistedStateKeys.SidePanelSearchPetNameSearch,
    '',
  );
  const [petParentNameSearch, setPetParentNameSearch] = usePersistedState<string>(
    PersistedStateKeys.SidePanelPetParentNameSearch,
    '',
  );
  const [phoneNumberSearch, setPhoneNumberSearch] = usePersistedState<string>(
    PersistedStateKeys.SidePanelPhoneNumberSearch,
    '',
  );
  const [isShowInactivePetsEnabled, setIsShowInactivePetsEnabled] = usePersistedState<boolean>(
    PersistedStateKeys.IsShowInactivePetsEnabled,
    false,
  );
  const [isSearchCollapsed, setIsSearchCollapsed] = useState<boolean>(false);
  const [currentView, setCurrentView] = usePersistedState<PanelViews>(
    PersistedStateKeys.SidePanelView,
    PanelViews.Search,
  );

  const [currentClinicPetParentId, setCurrentClinicPetParentId] = usePersistedState<string | null>(
    PersistedStateKeys.SidePanelCurrentClinicPetParentId,
    null,
  );

  const [currentClinicPetId, setCurrentClinicPetId] = usePersistedState<string | null>(
    PersistedStateKeys.SidePanelCurrentClinicPetId,
    null,
  );

  const [selectedClient, setSelectedClient] = usePersistedState<IAddClientOrPatient['selectedClinicPetParent'] | null>(
    PersistedStateKeys.SidePanelAddFormSelectedClient,
    null,
  );
  const [selectedPatient, setSelectedPatient] = usePersistedState<IAddClientOrPatient['selectedClinicPet'] | null>(
    PersistedStateKeys.SidePanelAddFormSelectedPatient,
    null,
  );

  const [openBenefitsModal, setOpenBenefitsModal] = useState<boolean>(false);

  const [removeInvoiceAttachment, setRemoveInvoiceAttachment] = usePersistedState<boolean>(
    PersistedStateKeys.RemoveInvoiceAttachment,
    false,
  );

  const [refreshUsages, setRefreshUsages] = usePersistedState<boolean>(PersistedStateKeys.RefreshUsages, false);

  const [invoiceId, setInvoiceId] = usePersistedState<string>(PersistedStateKeys.InvoiceId, '');
  const [viewBenefitSource, setViewBenefitSource] = usePersistedState<BenefitAlertSource>(
    PersistedStateKeys.ViewBenefitSource,
    BenefitAlertSource.MessageComposer,
  );

  const { isOpen, setIsOpen, setCurrentView: setSidePanelCurrentView, setIsZIndexIncreased } = useSidePanel();

  const [onAddClientClickOverride, setOnAddClientClickOverride] = useState<
    | {
      callback: (client: SidePanelPetParentFragment) => void;
    }
    | undefined
  >(undefined);

  const closeSideBarPanel = useCallback((): void => {
    setConversationId('');
    setIsOpen(false);
  }, [setIsOpen]);

  const resetSearchState = useCallback(
    (options?: { isSearchCollapsed: boolean }): void => {
      setPetNameSearch('');
      setPetParentNameSearch('');
      setPhoneNumberSearch('');
      setIsSearching(false);
      setIsShowInactivePetsEnabled(false);
      setIsSearchCollapsed(typeof options?.isSearchCollapsed === 'boolean' ? options.isSearchCollapsed : true);
      setOpenBenefitsModal(false);
    },
    [setPetNameSearch, setPetParentNameSearch, setPhoneNumberSearch, setIsShowInactivePetsEnabled],
  );

  const viewSearch = useCallback((): void => {
    resetSearchState({ isSearchCollapsed: false });
    setCurrentClinicPetParentId(null);
    setCurrentClinicPetId(null);
    setCurrentView(PanelViews.Search);
  }, [resetSearchState, setCurrentClinicPetId, setCurrentClinicPetParentId, setCurrentView]);

  const viewClinicPetParent = ({
    clinicPetParentId,
    forceOpen,
    isPanelZIndexIncreased,
    clearConversationId,
  }: {
    clinicPetParentId: string;
    mode?: Mode;
    forceOpen?: boolean;
    isPanelZIndexIncreased?: boolean;
    clearConversationId?: boolean;
  }): void => {
    try {
      resetSearchState();
      setCurrentClinicPetParentId(clinicPetParentId);
      setCurrentView(PanelViews.PetParentProfile);
      setSidePanelCurrentView(PanelViews.Search);
      if (clearConversationId) {
        setConversationId('');
      }
      if (isPanelZIndexIncreased) {
        setIsZIndexIncreased(true);
      }
      if (forceOpen !== false) {
        setIsOpen(true);
      }
    } catch (error) {
      Sentry.captureException(error);
      console.error(error);
    }
  };

  const viewClinicPet = ({
    clinicPetId,
    forceOpen,
    isPanelZIndexIncreased,
  }: {
    clinicPetId: string;
    forceOpen?: boolean;
    isPanelZIndexIncreased?: boolean;
  }): void => {
    try {
      resetSearchState();
      setCurrentClinicPetId(clinicPetId);
      setCurrentView(PanelViews.PetProfile);
      setSidePanelCurrentView(PanelViews.Search);
      if (isPanelZIndexIncreased) {
        setIsZIndexIncreased(true);
      }
      if (forceOpen !== false) {
        setIsOpen(true);
      }
    } catch (error) {
      Sentry.captureException(error);
      console.error(error);
    }
  };

  const viewBenefits = ({
    openBenefitsModal,
    clinicPetParentId,
  }: {
    openBenefitsModal?: boolean;
    clinicPetParentId: string;
  }): void => {
    try {
      resetSearchState();
      setCurrentView(PanelViews.Benefits);
      setSidePanelCurrentView(PanelViews.Search);
      setCurrentClinicPetParentId(clinicPetParentId);
      if (openBenefitsModal) setOpenBenefitsModal(true);

      if (!isOpen) {
        setIsOpen(true);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const [selectedLoyaltyRewardsTab, setSelectedLoyaltyRewardsTab] = useState<RewardsTab>(RewardsTab.RewardsHistory);

  const viewLoyaltyRewards = (tab: RewardsTab): void => {
    setSelectedLoyaltyRewardsTab(tab);
    setCurrentView(PanelViews.LoyaltyRewards);
  };

  const removeInvoice = ({ removeInvoiceAttachment }: { removeInvoiceAttachment: boolean }): void => {
    if (removeInvoiceAttachment) setRemoveInvoiceAttachment(true);
  };

  const viewAddClientOrPatient = useCallback(
    (options?: IAddClientOrPatient) => {
      const {
        selectedClinicPet,
        selectedClinicPetParent,
        conversationId,
        isFromInvoice,
        isPanelZIndexIncreased,
        onClientAdd,
      } = options || {};
      setSelectedClient(selectedClinicPetParent || null);
      setSelectedPatient(selectedClinicPet || null);
      setConversationId(conversationId || '');
      if (!isFromInvoice) {
        setCurrentView(PanelViews.AddClientPatientForm);
        setSidePanelCurrentView(PanelViews.Search);
      }
      resetSearchState();
      setOnAddClientClickOverride(onClientAdd);
      setSearchPanelMode(!!conversationId ? Mode.LinkToChannel : Mode.Default);
      if (isPanelZIndexIncreased) {
        setIsZIndexIncreased(true);
      }
      setIsOpen(true);
    },
    [
      setSelectedClient,
      setSelectedPatient,
      resetSearchState,
      setSearchPanelMode,
      setIsOpen,
      setIsZIndexIncreased,
      setCurrentView,
      setSidePanelCurrentView,
      setOnAddClientClickOverride,
    ],
  );

  const resetToDefaultSearchView = useCallback(() => {
    setSearchPanelMode(Mode.Default);
    viewSearch();
  }, [setSearchPanelMode, viewSearch]);

  /**
   * When switching the current clinic, reset to the empty search side panel
   */
  const { currentClinicId } = useClinicUser();
  const previousClinicId = usePrevious(currentClinicId);

  const { hasModule, loaded: modulesLoaded } = useModularity();
  const isStandardClinic = hasModule(ModuleName.AppointmentsStandard);

  useEffect(() => {
    if (!!previousClinicId && currentClinicId !== previousClinicId) {
      resetToDefaultSearchView();
    }
  }, [currentClinicId, previousClinicId, resetToDefaultSearchView]);

  // Ensure that when the AppointmentsStandard module is unavailable (for example, after switching from a standard to a non-standard clinic),
  // the side panel reverts to Default mode instead of remaining in the Search view from a previous session
  useEffect(() => {
    if (modulesLoaded && !isStandardClinic && currentView !== PanelViews.None && currentView !== PanelViews.Scheduler) {
      setSearchPanelMode(Mode.Default);
    }
  }, [currentView, isStandardClinic, modulesLoaded, setSearchPanelMode]);

  return (
    <SearchPanelContext.Provider
      value={{
        conversationId,
        isSearching,
        setIsSearching,
        petNameSearch,
        setPetNameSearch,
        petParentNameSearch,
        setPetParentNameSearch,
        phoneNumberSearch,
        setPhoneNumberSearch,
        isSearchCollapsed,
        isShowInactivePetsEnabled,
        setIsShowInactivePetsEnabled,
        setIsSearchCollapsed,
        currentView,
        currentClinicPetParentId,
        setCurrentClinicPetParentId,
        currentClinicPetId,
        setCurrentClinicPetId,
        selectedClient,
        selectedPatient,
        searchPanelMode,
        setSearchPanelMode,
        viewSearch,
        viewClinicPetParent,
        viewClinicPet,
        openBenefitsModal,
        setOpenBenefitsModal,
        refreshUsages,
        setRefreshUsages,
        invoiceId,
        setInvoiceId,
        viewBenefitSource,
        setViewBenefitSource,
        viewBenefits,
        viewAddClientOrPatient,
        removeInvoiceAttachment,
        setRemoveInvoiceAttachment,
        removeInvoice,
        viewLoyaltyRewards,
        selectedLoyaltyRewardsTab,
        setSelectedLoyaltyRewardsTab,
        onAddClientClickOverride,
        setOnAddClientClickOverride,
        resetToDefaultSearchView,
        closeSideBarPanel,
      }}
    >
      {children}
    </SearchPanelContext.Provider>
  );
};

export default SearchPanelProvider;
